import { RootState } from '@Common/models/redux/store';
import { openToastr } from '@Common/handlers/services';
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  Theme,
  useTheme,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { FileCopyOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { copyRichTextToClip, copyTextToClipboard } from 'core/utils';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  cursorPointer,
  displayFlex,
  displayNone,
  fontSize,
  fontWeight,
  primaryColor,
  unitPX,
} from 'src/researcher/styles';
import { BIBLIOGRAPHY_PAGE_ID } from 'src/researcher/constants/common.constants';
import BibliographyPageEncouragementTooltip from 'src/researcher/component/BibliographyPageEncouragementTooltip';

import {
  CitationDocument,
  downloadFile,
  exportToBibtex,
  exportToCslJson,
  exportToDocx,
  getCgBreakpointUp,
} from '../utils';
import { CitationData } from '../types';
import { citationsRichTextWithParagraph } from './CitationElement';
import citationGeneratorTrack from '../handlers/track';
import { CITATION_EXPORT_CONTROL_HEIGHT } from '../constants/common.constants';
import { ViewWorkCitedTooltipText } from '../constants/message.constant';

const exportFormats = [
  {
    name: 'MS Word',
    ext: '.docx',
    value: 'ms-word',
  },
  {
    name: 'BibTeX',
    ext: '.bib',
    value: 'bibtex',
  },
  {
    name: 'CSL-JSON',
    ext: '.json',
    value: 'csl-json',
  },
];

interface StyleProps {
  isCowriter: boolean;
  isDrawerOpen: boolean;
}
const useStyles = makeStyles((theme) => ({
  helpText: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  container: {
    height: unitPX(CITATION_EXPORT_CONTROL_HEIGHT),
    marginTop: '2px',
    width: ({ isCowriter }: StyleProps) => (isCowriter ? 'calc(100%)' : 'calc(100% - 1px)'),
    marginLeft: ({ isCowriter }: StyleProps) => (isCowriter ? '0px' : undefined),
    backgroundColor: theme.palette.background.paper,
    padding: '0 16px',
  },
  containerShadow: {
    boxShadow: '0px -4px 10px rgb(0 0 0 / 5%)',
  },
  copyIcon: {
    fontSize: 18,
    verticalAlign: 'middle',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  iconWrapper: {
    padding: 4,
    cursor: 'pointer',
    marginLeft: 5,
  },
  btnPill: {
    borderRadius: 20,
    fontSize: '12px',
    fontWeight: 600,
    borderColor: theme.palette.primary.main,
    height: '26px',
    [theme.breakpoints.down(1550)]: {
      padding: ({ isDrawerOpen }: StyleProps) => (isDrawerOpen ? '2px 8px' : '6px 16px'),
    },
  },
  btnDownload: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  downloadBtnContainer: {
    [theme.breakpoints.down(1100)]: {
      display: 'none',
    },
  },
  downloadBtnCompact: {
    '& > button': {
      width: '78px',
    },
    [theme.breakpoints.up(1100)]: {
      display: 'none',
    },
  },
  btnCopyAll: {
    color: theme.palette.primary.contrastText,
  },
  compactIcon: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 2,
    boxsizing: 'border-box',
    height: 40,
    width: 40,
    '&:not(:first-child)': {
      marginLeft: 2,
    },
  },
  compactDownloadMenuItem: {
    fontSize: '13px',
    backgroundColor: '#f1f1f1',
    paddingTop: 0,
    paddingBottom: 0,
    [getCgBreakpointUp(theme)]: {
      backgroundColor: theme.palette.background.paper,
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
}));

const ExportCitation = dynamic(() => import('./ExportCitation'), {
  ssr: false,
});

export default function ExportCitationsBar({
  citations,
  isCompact,
}: {
  citations: CitationData[];
  isCompact?: boolean;
}) {
  const router = useRouter();
  const theme = useTheme();
  const isCowriter = router.pathname.includes('/co-writer');
  const quillDrawer = useSelector((state: RootState) => state.common.quillDrawer);
  const isIE = useSelector((state: RootState) => state.common.platform.isIE);
  const classes = useStyles({ isCowriter, isDrawerOpen: !!quillDrawer });
  const citedDataById = useSelector((state: RootState) => state.ctgr.citedDataById);
  const citationStyle = useSelector((state: RootState) => state.ctgr.citationStyle);
  const [citationDocument, setCitationDocument] = useState<CitationDocument | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const download = (type: string) => {
    const citedData = citations.map((citation) => citedDataById[citation.refId]);

    switch (type) {
      case 'ms-word': {
        setCitationDocument(exportToDocx(citedData, citationStyle));
        break;
      }
      case 'bibtex': {
        downloadFile(exportToBibtex(citedData), 'Citations_Export.bib', 'application/x-bibtex');
        break;
      }
      case 'csl-json': {
        downloadFile(exportToCslJson(citedData), 'Citations_Export.json');
        break;
      }
      default:
        break;
    }

    citationGeneratorTrack.userClickedExportButton(citationStyle, type);
  };

  const copyToClipboard = (type?: string) => {
    const citedData = citations.map((citation) => citedDataById[citation.refId]);
    switch (type) {
      case 'bibtex':
        copyTextToClipboard(
          exportToBibtex(citedData),
          () => openToastr('Full citation(s) copied to clipboard!'),
          () => openToastr('Failed to copy!')
        );
        break;
      case 'csl-json':
        copyTextToClipboard(
          exportToCslJson(citedData),
          () => openToastr('Full citation(s) copied to clipboard!'),
          () => openToastr('Failed to copy!')
        );
        break;
      default:
        copyRichTextToClip(
          citationsRichTextWithParagraph(citedData),
          () => openToastr('Full citation(s) copied to clipboard!'),
          () => openToastr('Failed to copy!')
        );
        break;
    }
    citationGeneratorTrack.userClickedCopyAllCitationsButton(citationStyle);
  };

  const isVisibleShadow = () => {
    const citationsContainer = document.getElementById('citationCardsContainerId');
    return (
      Number(citationsContainer?.clientHeight) >
      Number(citationsContainer?.parentElement?.clientHeight)
    );
  };

  if (isIE) {
    return null;
  }

  return (
    <>
      <ExportCitation doc={citationDocument} />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        elevation={4}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {exportFormats.map((format) => (
          <MenuItem
            key={format.value}
            className={classes.compactDownloadMenuItem}
            onClick={() => {
              download(format.value);
              setAnchorEl(null);
            }}
          >
            {`${format.name} (${format.ext})`}
          </MenuItem>
        ))}
      </Popover>

      {isCompact ? (
        <Grid ml="4px">
          <IconButton
            className={classes.compactIcon}
            onClick={() => {
              copyToClipboard();
            }}
          >
            <FileCopyOutlined color="primary" />
          </IconButton>

          <IconButton
            className={classes.compactIcon}
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
          >
            <FileDownloadOutlined color="primary" />
          </IconButton>
        </Grid>
      ) : (
        <Grid
          container
          className={classes.container}
          style={{
            boxShadow: isVisibleShadow() ? '0px -4px 10px rgb(0 0 0 / 5%)' : undefined,
          }}
          justifyContent="space-between"
          alignItems="center"
        >
          {!isCowriter && (
            <Grid item alignItems="center" style={{ display: 'flex' }}>
              <span className={classes.helpText}>Download As:</span>

              <Grid className={classes.downloadBtnContainer}>
                {exportFormats.map((format) => (
                  <Button
                    key={format.value}
                    className={`${classes.btnPill} ${classes.btnDownload}`}
                    color="primary"
                    variant="outlined"
                    onClick={() => download(format.value)}
                  >
                    {format.name}
                  </Button>
                ))}
              </Grid>

              <Grid className={classes.downloadBtnCompact}>
                <Button
                  color="primary"
                  variant="outlined"
                  className={`${classes.btnPill} ${classes.btnDownload}`}
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                  }}
                >
                  <FileDownloadOutlined color="primary" />
                </Button>
              </Grid>
            </Grid>
          )}

          {isCowriter && <WorkCitedAnchor />}

          <Grid item>
            <Tooltip title="Copy all full citations" placement="top">
              <Button
                className={`${classes.btnPill} ${classes.btnCopyAll}`}
                color="primary"
                variant={isCowriter ? 'outlined' : 'contained'}
                onClick={() => {
                  copyToClipboard();
                }}
                style={{ ...(isCowriter ? { color: theme.palette.primary.main } : null) }}
              >
                Copy All
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      )}
    </>
  );
}

function WorkCitedAnchor() {
  const workCitedCount = useSelector(
    (state: RootState) =>
      state.ctgr.citationsFromCowriter?.filter((citation) => citation.isCited)?.length || 0
  );

  const hasWorkCited = Boolean(workCitedCount);

  return (
    <>
      {/* <BibliographyPageEncouragementTooltip showScrollToBottom={hasWorkCited} /> */}
      <Tooltip arrow placement="top" title={ViewWorkCitedTooltipText}>
        <Grid item>
          <Typography
            onClick={() =>
              document.getElementById(BIBLIOGRAPHY_PAGE_ID)?.scrollIntoView({ behavior: 'smooth' })
            }
            component="span"
            sx={[
              fontSize[16],
              fontWeight[600],
              { color: (t: Theme) => t.palette.text.secondary },
              cursorPointer,
              hasWorkCited ? displayFlex : displayNone,
            ]}
          >
            View Works Cited&nbsp;
            <Typography component="span" sx={primaryColor}>
              ({workCitedCount})
            </Typography>
          </Typography>
        </Grid>
      </Tooltip>
    </>
  );
}
